import React, { useEffect, useState, useContext } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSection } from 'components/UI';
import Images from 'assets/images/imageData';
import localization from 'assets/localization/localization';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import {
  fetchSalesTargetsList,
  fetchLatestSalesTargetPeriod,
  fetchSalesTargetPeriodById,
  closeAndCreateNextPeriod,
} from 'services/api/salesTargetsApi';
import SetTargetsPopup from './SetTargetsPopup';
import AddAchievementPopup from './AddAchievementPopup';
import UpdateEndDatePopup from './UpdateEndDatePopup';
import PeriodSummary from './PeriodSummary';
import { MONTH_NAMES, buildPeriodRangeLabel, formatShortDate } from './salesTargetsPeriodUtils';
import './salesTargetsListing.css';

const LIMIT = 10;
const LIST_TYPE = {
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
  ADDITIONAL: 'additional',
};

const INITIAL_MONTHLY_FILTERS = { period_id: '', month: '', year: '', search: '' };
const INITIAL_WEEKLY_FILTERS = { period_id: '', search: '' };
const INITIAL_ADDITIONAL_FILTERS = { period_id: '', month: '', year: '', search: '' };

const formatAmountInLacs = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  const lacs = num / 100000;
  return `${lacs.toFixed(2)} Lac`;
};

const formatTargetLakhs = (row) => {
  if (row?.target_lakhs != null && row.target_lakhs !== '') {
    const num = Number(row.target_lakhs);
    if (!Number.isNaN(num)) return `${num.toFixed(2)} Lac`;
  }
  return formatAmountInLacs(row?.target_value);
};

const formatMonthYear = (month, year) => {
  if (!month || !year) return '—';
  const monthName = MONTH_NAMES[month];
  return monthName ? `${monthName} ${year}` : '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  return formatShortDate(value) || '—';
};

const getInitialFilters = (listType) => {
  if (listType === LIST_TYPE.WEEKLY) return { ...INITIAL_WEEKLY_FILTERS };
  if (listType === LIST_TYPE.ADDITIONAL) return { ...INITIAL_ADDITIONAL_FILTERS };
  return { ...INITIAL_MONTHLY_FILTERS };
};

const MONTHLY_COLUMNS = [
  { key: 'user_name', label: 'User Name' },
  { key: 'user_email', label: 'Email' },
  {
    key: 'target_month',
    label: 'Month / Year',
    format: (_, row) => formatMonthYear(row?.target_month, row?.target_year),
  },
  {
    key: 'target_value',
    label: 'Amount (Lacs)',
    format: (val) => formatAmountInLacs(val),
  },
];

const WEEKLY_COLUMNS = [
  { key: 'user_name', label: 'User Name' },
  { key: 'user_email', label: 'Email' },
  {
    key: 'week_start',
    label: 'Week Start',
    format: (val) => formatDate(val),
  },
  {
    key: 'week_end',
    label: 'Week End',
    format: (val) => formatDate(val),
  },
  {
    key: 'target_value',
    label: 'Amount (Lacs)',
    format: (_, row) => formatTargetLakhs(row),
  },
];

const ADDITIONAL_COLUMNS = [
  { key: 'user_name', label: 'User Name' },
  { key: 'user_email', label: 'Email' },
  {
    key: 'period_month',
    label: 'Month / Year',
    format: (_, row) => formatMonthYear(row?.period_month, row?.period_year),
  },
  {
    key: 'amount',
    label: 'Amount (Lacs)',
    format: (val) => formatAmountInLacs(val),
  },
  { key: 'reason', label: 'Reason' },
  { key: 'reference_id', label: 'Reference ID' },
  { key: 'added_by_name', label: 'Added By' },
  {
    key: 'created_at',
    label: 'Created On',
    format: (val) => formatDate(val),
  },
];

const SalesTargetsListing = () => {
  const { baseUrl } = useContext(BaseUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [listType, setListType] = useState(LIST_TYPE.MONTHLY);
  const [filterData, setFilterData] = useState(INITIAL_MONTHLY_FILTERS);
  const [showFilter, setShowFilter] = useState(false);
  const [listData, setListData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSetTargetsPopup, setShowSetTargetsPopup] = useState(false);
  const [showAddAchievementPopup, setShowAddAchievementPopup] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState(null);
  const [hasMonthlyTargetsForPeriod, setHasMonthlyTargetsForPeriod] = useState(false);
  const [showUpdateEndPopup, setShowUpdateEndPopup] = useState(false);

  const isWeekly = listType === LIST_TYPE.WEEKLY;
  const isAdditional = listType === LIST_TYPE.ADDITIONAL;
  const pageCount = total ? Math.ceil(total / LIMIT) : 0;
  const from = (currentPage - 1) * LIMIT + 1;
  const tableColumns = isWeekly
    ? WEEKLY_COLUMNS
    : isAdditional
      ? ADDITIONAL_COLUMNS
      : MONTHLY_COLUMNS;

  const getPeriodMonthYear = (period = currentPeriod) => {
    if (!period) return { month: null, year: null };
    const month = period.period_month ?? period.target_month;
    const year = period.period_year ?? period.target_year;
    if (month == null || month === '' || year == null || year === '') {
      return { month: null, year: null };
    }
    return { month: Number(month), year: Number(year) };
  };

  const checkMonthlyTargetsForPeriod = async (period) => {
    const { month, year } = getPeriodMonthYear(period);
    if (!month || !year) {
      setHasMonthlyTargetsForPeriod(false);
      return false;
    }
    try {
      const { total: totalCount } = await fetchSalesTargetsList(1, 1, {
        type: LIST_TYPE.MONTHLY,
        month: String(month),
        year: String(year),
      });
      const hasTargets = Number(totalCount) > 0;
      setHasMonthlyTargetsForPeriod(hasTargets);
      return hasTargets;
    } catch (err) {
      setHasMonthlyTargetsForPeriod(false);
      return false;
    }
  };

  const fetchTargets = async (page = 1, filters = filterData, type = listType) => {
    setIsLoading(true);
    try {
      const { results, total: totalCount } = await fetchSalesTargetsList(page, LIMIT, {
        ...filters,
        type,
      });
      setListData(results);
      setTotal(totalCount);
      setCurrentPage(page);
    } catch (err) {
      setListData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFilterPeriod = async (periodId) => {
    if (!periodId) {
      setFilterPeriod(null);
      return null;
    }
    if (currentPeriod?.id != null && String(currentPeriod.id) === String(periodId)) {
      setFilterPeriod(null);
      return currentPeriod;
    }
    try {
      const period = await fetchSalesTargetPeriodById(periodId);
      setFilterPeriod(period);
      return period;
    } catch {
      setFilterPeriod(null);
      return null;
    }
  };

  const loadCurrentPeriod = async () => {
    try {
      const period = await fetchLatestSalesTargetPeriod();
      setCurrentPeriod(period);
      return period;
    } catch (err) {
      setCurrentPeriod(null);
      setHasMonthlyTargetsForPeriod(false);
      return null;
    }
  };

  const openSetTargetsPopup = async () => {
    setShowSetTargetsPopup(true);
    await checkMonthlyTargetsForPeriod(currentPeriod);
  };

  useEffect(() => {
    fetchTargets(1, INITIAL_MONTHLY_FILTERS, LIST_TYPE.MONTHLY);
    loadCurrentPeriod();
  }, []);

  const handleListTypeChange = (nextType) => {
    if (nextType === listType) return;
    const nextFilters = getInitialFilters(nextType);
    setListType(nextType);
    setFilterData(nextFilters);
    setFilterPeriod(null);
    setShowFilter(false);
    fetchTargets(1, nextFilters, nextType);
  };

  const onPageChange = async (val) => {
    const nextPage = (val?.selected ?? 0) + 1;
    await fetchTargets(nextPage, filterData, listType);
  };

  const filterToggle = () => {
    setShowFilter(!showFilter);
  };

  const applyFilter = async (filterPayload) => {
    setFilterData(filterPayload);
    await loadFilterPeriod(filterPayload?.period_id);
    await fetchTargets(1, filterPayload, listType);
    filterToggle();
  };

  const removeFilter = () => {
    const cleared = getInitialFilters(listType);
    setFilterData(cleared);
    setFilterPeriod(null);
    fetchTargets(1, cleared, listType);
  };

  const getPeriodLabel = (period) => {
    if (!period) return '';
    const m = period.period_month ?? period.target_month;
    const y = period.period_year ?? period.target_year;
    if (m == null || m === '' || y == null || y === '') return '';
    return formatMonthYear(Number(m), Number(y));
  };

  const appliedFilterLabels = [];
  if (filterData.period_id) {
    const label =
      getPeriodLabel(filterPeriod) ||
      getPeriodLabel(
        currentPeriod?.id != null && String(currentPeriod.id) === String(filterData.period_id)
          ? currentPeriod
          : null
      ) ||
      `Period #${filterData.period_id}`;
    appliedFilterLabels.push(`Period: ${label}`);
  }
  if (!isWeekly && !filterData.period_id) {
    if (filterData.month) {
      appliedFilterLabels.push(`Month: ${MONTH_NAMES[filterData.month] || filterData.month}`);
    }
    if (filterData.year) appliedFilterLabels.push(`Year: ${filterData.year}`);
  }
  if (filterData.search && filterData.search.trim()) {
    appliedFilterLabels.push(`Employee: ${filterData.search.trim()}`);
  }

  /** Uses API fields period_month / period_year (fallback: target_month / target_year). */
  const getPeriodMonthYearLabel = () => getPeriodLabel(currentPeriod);

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: 'Set Targets',
        onClick: openSetTargetsPopup,
        icon: Images?.Calendar,
      },
      {
        label: 'Add Achievement',
        onClick: () => setShowAddAchievementPopup(true),
        icon: Images?.DocIcon,
      },
      {
        label: 'Update End Date',
        onClick: () => setShowUpdateEndPopup(true),
        icon: Images?.Calendar,
      },
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.Filter,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2 sales-targets-header-actions">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  const periodRangeLabel = buildPeriodRangeLabel(currentPeriod);
  const periodWeeks = Array.isArray(currentPeriod?.weeks) ? currentPeriod.weeks : [];
  const periodMonthYearLabel = getPeriodMonthYearLabel();
  const filterPeriodRangeLabel = buildPeriodRangeLabel(filterPeriod);
  const filterPeriodWeeks = Array.isArray(filterPeriod?.weeks) ? filterPeriod.weeks : [];
  const filterPeriodMonthLabel = getPeriodLabel(filterPeriod);
  const showFilteredPeriod =
    Boolean(filterData.period_id) &&
    Boolean(filterPeriod) &&
    String(filterPeriod.id) !== String(currentPeriod?.id);

  return (
    <Layout
      className="sales-targets-listing-page"
      title={localization.targets}
      Buttons={headerBtns}
      isGoback
      isLoading={isLoading}
      linkFlag
      linkUrl={`${baseUrl}/dashboard`}
      useNewHeaderLayout
    >
      <div className="sales-targets-list-toolbar">
        <PeriodSummary
          monthLabel={periodMonthYearLabel}
          periodRangeLabel={periodRangeLabel}
          weeks={periodWeeks}
        />

        {showFilteredPeriod && (
          <div className="sales-targets-filtered-period">
            <span className="sales-targets-filtered-period-label">Filtered period</span>
            <PeriodSummary
              monthLabel={filterPeriodMonthLabel}
              periodRangeLabel={filterPeriodRangeLabel}
              weeks={filterPeriodWeeks}
            />
          </div>
        )}

        <div className="sales-targets-list-tabs" role="tablist" aria-label="Target list type">
          <button
            type="button"
            role="tab"
            aria-selected={listType === LIST_TYPE.MONTHLY}
            className={`sales-targets-list-tab${listType === LIST_TYPE.MONTHLY ? ' is-active' : ''}`}
            onClick={() => handleListTypeChange(LIST_TYPE.MONTHLY)}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={listType === LIST_TYPE.WEEKLY}
            className={`sales-targets-list-tab${listType === LIST_TYPE.WEEKLY ? ' is-active' : ''}`}
            onClick={() => handleListTypeChange(LIST_TYPE.WEEKLY)}
          >
            Weekly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={listType === LIST_TYPE.ADDITIONAL}
            className={`sales-targets-list-tab${listType === LIST_TYPE.ADDITIONAL ? ' is-active' : ''}`}
            onClick={() => handleListTypeChange(LIST_TYPE.ADDITIONAL)}
          >
            Achievements
          </button>
        </div>
      </div>

      <AppliedFiltersBar items={appliedFilterLabels} onClear={removeFilter} />
      <TableSection
        className="sales-targets-table-section"
        title={
          isWeekly ? 'Weekly targets' : isAdditional ? 'Additional achievements' : localization.targets
        }
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo: from, className: 'sales-targets-table-items' }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLoading={isLoading}
        emptyMessage={
          isWeekly
            ? 'No weekly sales targets found.'
            : isAdditional
              ? 'No additional achievements found.'
              : 'No sales targets found.'
        }
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={(val) => applyFilter(val)}
        salesTargetsFilter
        listFilterData={{ appliedFilterValues: filterData, listType }}
      />

      <AddAchievementPopup
        show={showAddAchievementPopup}
        onClose={() => setShowAddAchievementPopup(false)}
        onSuccess={async () => {
          await fetchTargets(currentPage, filterData, listType);
        }}
      />
      <SetTargetsPopup
        show={showSetTargetsPopup}
        onClose={() => setShowSetTargetsPopup(false)}
        hasMonthlyTargetsForPeriod={hasMonthlyTargetsForPeriod}
        periodMonth={getPeriodMonthYear().month}
        periodYear={getPeriodMonthYear().year}
        periodMonthLabel={getPeriodMonthYearLabel()}
        periodRangeLabel={periodRangeLabel}
        periodWeeks={periodWeeks}
        onSuccess={async (result) => {
          await fetchTargets(currentPage, filterData, listType);
          if (result?.type === LIST_TYPE.MONTHLY) {
            const { month, year } = getPeriodMonthYear();
            if (
              month &&
              year &&
              Number(result.month) === month &&
              Number(result.year) === year
            ) {
              setHasMonthlyTargetsForPeriod(true);
            } else {
              await checkMonthlyTargetsForPeriod(currentPeriod);
            }
          }
        }}
      />
      <UpdateEndDatePopup
        show={showUpdateEndPopup}
        onClose={() => setShowUpdateEndPopup(false)}
        currentPeriodRange={buildPeriodRangeLabel(currentPeriod)}
        periodMonthYearLabel={getPeriodMonthYearLabel()}
        periodWeeks={periodWeeks}
        onSubmit={async (date) => {
          await closeAndCreateNextPeriod(date.trim());
        }}
        onSuccess={async () => {
          await loadCurrentPeriod();
          if (filterData.period_id) {
            await loadFilterPeriod(filterData.period_id);
          }
          await fetchTargets(1, filterData, listType);
        }}
      />
    </Layout>
  );
};

export default SalesTargetsListing;
